/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 */

import React from "react";
import { Box, Divider } from "@mui/material";
import { startCase } from "lodash";

const IGNORED_KEYS = new Set(["id", "discount_card_id"]);

const isRenderableValue = (value) =>
  value !== null && value !== undefined && value !== "";

export const formatLogFieldLabel = (key) => startCase(key);

export const formatLogFieldValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  return isRenderableValue(value) ? `${value}` : "-";
};

export const normalizeLogEntry = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  const normalizedEntry = Object.entries(entry).reduce(
    (accumulator, [key, value]) => {
      if (IGNORED_KEYS.has(key) || !isRenderableValue(value)) {
        return accumulator;
      }

      accumulator[key] = value;
      return accumulator;
    },
    {},
  );

  return Object.keys(normalizedEntry).length ? normalizedEntry : null;
};

export const normalizeLogEntries = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map(normalizeLogEntry).filter(Boolean);
};

export const normalizeModifiedAttributes = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.filter((item) => item && item !== "discount_card_id");
};

const LogEntryBlock = ({ entry, showDivider }) => {
  const fields = Object.entries(entry);

  if (!fields.length) {
    return null;
  }

  return (
    <Box sx={{ py: 0.25 }}>
      {showDivider ? <Divider sx={{ my: 1, bgcolor: "#e0e0e0" }} /> : null}
      <ul style={{ padding: 0, margin: 0 }}>
        {fields.map(([key, value]) => (
          <li key={key} style={{ listStyle: "none", marginBottom: 4 }}>
            <span style={{ color: "#212121", fontWeight: 600 }}>
              {formatLogFieldLabel(key)}:
            </span>{" "}
            {formatLogFieldValue(value)}
          </li>
        ))}
      </ul>
    </Box>
  );
};

const buildLogEntryKey = (signature, occurrence) => {
  return `${signature}-${occurrence}`;
};

export const LogAttributes = ({ data }) => {
  const attributes = normalizeModifiedAttributes(data);

  if (!attributes.length) {
    return "-";
  }

  return (
    <Box>
      <ul style={{ padding: 0, margin: 0 }}>
        {attributes.map((attribute) => (
          <li
            key={attribute}
            style={{ listStyle: "none", marginBottom: 4 }}
            data-testid="discount-card-log-attribute"
          >
            {formatLogFieldLabel(attribute)}
          </li>
        ))}
      </ul>
    </Box>
  );
};

export const LogValueGroups = ({ data }) => {
  const entries = normalizeLogEntries(data);

  if (!entries.length) {
    return "-";
  }

  const entryCounts = new Map();

  return (
    <Box>
      {entries.map((entry, index) => {
        const signature = JSON.stringify(entry);
        const occurrence = entryCounts.get(signature) || 0;
        entryCounts.set(signature, occurrence + 1);

        return (
          <LogEntryBlock
            key={buildLogEntryKey(signature, occurrence)}
            entry={entry}
            showDivider={index > 0}
          />
        );
      })}
    </Box>
  );
};
